import {
  AnalyticsEvent,
  BatchConfig,
  UserActivityTrackCredentials,
} from "@plugins/user-activity-track/types.ts";
import { notDefined } from "@utils/var.check";
import DOMUtils from "@utils/dom.utils";
import { DEFAULT_CONFIG } from "@plugins/user-activity-track/service/user.activity.track.constant";

export abstract class AbstractMessageBatchProcessor {
  protected eventQueue: AnalyticsEvent[] = [];
  protected flushTimeout: NodeJS.Timeout | null = null;
  protected config: BatchConfig = DEFAULT_CONFIG as BatchConfig;

  protected constructor() {
    if (notDefined(window)) {
      return;
    }

    const flushPendingEvents = (): void => {
      if (this.eventQueue.length > 0) {
        this.clearScheduledFlush();
        void this.flush(true).catch(() => {
          console.error("Unable to flush user activity events at session end");
        });
      }
    };

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushPendingEvents();
      }
    });

    DOMUtils.addBeforeUnloadEventListener(flushPendingEvents);
  }

  public setBatchConfig(config: BatchConfig | null): void {
    this.config = !config ? { ...(DEFAULT_CONFIG as BatchConfig) } : config;
  }

  abstract flush(flushAll): Promise<void>;

  abstract reinitialize(credentials: UserActivityTrackCredentials);

  async addEvent(event: AnalyticsEvent, immediateFlush = false): Promise<void> {
    this.eventQueue.push(event);

    if (immediateFlush) {
      await this.flush(true);
    } else {
      await this.scheduleFlush();
    }
  }

  protected async scheduleFlush(): Promise<void> {
    // Immediate flush if batch size reached
    if (this.eventQueue.length >= this.config.batchSize) {
      this.clearScheduledFlush();
      await this.flush(false);
      return;
    }

    // Schedule delayed flush if not already scheduled
    if (!this.flushTimeout && this.eventQueue.length > 0) {
      this.flushTimeout = setTimeout(async () => {
        this.flushTimeout = null;
        await this.flush(false);
      }, this.config.batchDelay);
    }
  }

  protected clearScheduledFlush(): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
  }
}
